import {
  RiArrowDownFill,
  RiArrowUpFill,
  RiCheckboxCircleFill,
  RiCircleFill,
  RiErrorWarningFill,
  RiPlayCircleFill,
  RiStopCircleFill,
} from "react-icons/ri";
import { SortableHeader, useSorting } from "@/Utility/Sorting";
import Loader from "@/Utility/Loader/Loader";
import Typography from "@/Utility/Typography";
import NoDataFound from "@/Utility/NoDataFound/NoDataFound";

const BacklogList = ({
  issuesData = [],
  loading = false,
  searchTerm = "",
  onRequestEditWorkItem,
}) => {
  /* ================= FILTER ================= */
  const filteredIssues = issuesData.filter((issue) => {
    const matchesSearch =
      !searchTerm ||
      issue.issueKey?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.assignee?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.priority?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.status?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  /* ================= SORTING ================= */
  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(filteredIssues, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });
  const getPriorityBadgeStyle = (priority) => {
    const styles = {
      low: {
        backgroundColor: "#E8F8F0",
        color: "#0F9D58",
        borderColor: "#B7E4C7",
      },
      medium: {
        backgroundColor: "#FFF4E5",
        color: "#F59E0B",
        borderColor: "#FCD34D",
      },
      high: {
        backgroundColor: "#FDECEC",
        color: "#DC2626",
        borderColor: "#FCA5A5",
      },
      critical: {
        backgroundColor: "#FCE8E8",
        color: "#991B1B",
        borderColor: "#F87171",
      },
    };

    return (
      styles[priority?.toLowerCase()] || {
        backgroundColor: "#F3F4F6",
        color: "#6B7280",
        borderColor: "#D1D5DB",
      }
    );
  };

  /* PRIORITY ICON */
  const getPriorityIcon = (priority) => {
    const priorityLower = priority?.toLowerCase();
    switch (priorityLower) {
      case "low":
        return <RiArrowDownFill className="text-xs" />;
      case "medium":
        // Using a simple dash character since icon might not be available
        return <RiArrowUpFill className="text-xs" />;
      case "high":
        return <RiArrowUpFill className="text-xs" />;
      case "critical":
        return <RiErrorWarningFill className="text-xs" />;
      default:
        return null;
    }
  };

  const getStatusBadgeStyle = (status) => {
    const styles = {
      open: {
        backgroundColor: "#EEF2FF",
        color: "#4338CA",
        borderColor: "#C7D2FE",
      },
      "to do": {
        backgroundColor: "#EEF2FF",
        color: "#4338CA",
        borderColor: "#C7D2FE",
      },
      new: {
        backgroundColor: "#EEF2FF",
        color: "#4338CA",
        borderColor: "#C7D2FE",
      },
      "in progress": {
        backgroundColor: "#F0F9FF",
        color: "#0284C7",
        borderColor: "#BAE6FD",
      },
      blocked: {
        backgroundColor: "#FEF2F2",
        color: "#B91C1C",
        borderColor: "#FECACA",
      },
      done: {
        backgroundColor: "#ECFDF5",
        color: "#047857",
        borderColor: "#A7F3D0",
      },
    };

    return (
      styles[status?.toLowerCase()] || {
        backgroundColor: "#F3F4F6",
        color: "#6B7280",
        borderColor: "#D1D5DB",
      }
    );
  };

  /* STATUS ICON */
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
      case "to do":
        return <RiCircleFill className="text-xs" />;
      case "in progress":
        return <RiPlayCircleFill className="text-xs" />;
      case "blocked":
        return <RiStopCircleFill className="text-xs" />;
      case "done":
        return <RiCheckboxCircleFill className="text-xs" />;
      default:
        return null;
    }
  };

  return (
    <div className="clients-page-content !bg-transparent">
      <div className="w-full">
        <div className="relative flex flex-col min-w-0 bg-surface-primary shadow-sm transition-shadow">
          <div className="flex-1 p-2">
            <div className="live-preview">
              <div className="overflow-x-auto">
                {/* ✅ SAME TABLE CLASS */}
                <table className="w-full mb-0 clients-table">
                  <thead className="bg-surface-secondary">
                    <tr>
                      <SortableHeader
                        columnKey="issueKey"
                        label="Issue"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="assignee"
                        label="Assignee"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="priority"
                        label="Priority"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="dueDate"
                        label="Due Date"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader columnKey="status" label="Status" sortable={false} />
                    </tr>
                  </thead>

                  <tbody className={`relative ${loading ? "h-300" : ""}`}>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="text-center">
                          <Loader />
                        </td>
                      </tr>
                    ) : sortedData.length > 0 ? (
                      sortedData.map((issue) => {
                        const canEditHere = typeof onRequestEditWorkItem === "function";
                        const openEdit = () => canEditHere && onRequestEditWorkItem(issue);
                        return (
                          <tr
                            key={issue.id}
                            className={
                              canEditHere
                                ? "cursor-pointer hover:bg-gray-50/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                                : undefined
                            }
                            tabIndex={canEditHere ? 0 : undefined}
                            onClick={canEditHere ? openEdit : undefined}
                            onKeyDown={
                              canEditHere
                                ? (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      openEdit();
                                    }
                                  }
                                : undefined
                            }
                            aria-label={canEditHere ? "Edit work item" : undefined}
                          >
                            {/* ISSUE */}
                            <td className="text-start align-middle">
                              <Typography
                                variant="span1"
                                className="cursor-pointer underline hover:opacity-80 text-text-link"
                              >
                                <Typography variant="span1" className="mr-2" component="span">
                                  {issue.issueKey}
                                </Typography>
                                {issue.title}
                              </Typography>
                            </td>

                            {/* ASSIGNEE */}
                            <td className="text-start align-middle">
                              <Typography variant="span1">
                                {issue.assignee || "Unassigned"}
                              </Typography>
                            </td>

                            <td className="text-start align-middle px-3 py-3">
                              <Typography
                                variant="caption"
                                component="span"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded font-medium cursor-pointer border"
                                style={getPriorityBadgeStyle(issue.priority)}
                              >
                                {getPriorityIcon(issue.priority)}
                                {issue.priority || "N/A"}
                              </Typography>
                            </td>

                            {/* DUE DATE */}
                            <td className="text-start align-middle">
                              <Typography variant="span1">{issue.dueDate || "-"}</Typography>
                            </td>

                            {/* STATUS */}
                            <td className="text-start align-middle px-3 py-3">
                              <Typography
                                variant="caption"
                                component="span"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded font-medium cursor-pointer border"
                                style={getStatusBadgeStyle(issue.status)}
                              >
                                {getStatusIcon(issue.status)}
                                {issue.status || "N/A"}
                              </Typography>
                            </td>

                            {/* ACTION */}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center">
                          <NoDataFound
                            message={
                              searchTerm
                                ? "No backlog issues found matching your search"
                                : "No backlog issues available"
                            }
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center mt-2 text-center sm:text-start" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BacklogList;
